import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from 'services/api';
import { flattenQuery } from 'helpers/flattenQuery';
import { FilterConfig } from 'hooks/useListQuery';
import { extractErrorMessage } from 'helpers/extractErrorMessage';

// Define the shape of a User
interface User {
  user_id: number;
  username?: string;
  password?: string; // Password is optional when fetching users
  public_key?: string; // Add public_key here as optional
  mail?: string;
  user_settings?: Record<string, any>;
  user_config?: Record<string, any>;
  privileges?: any[];
  roles?: { role_id: number; name: string }[];
  role_ids?: number[];
}

interface PaginationState {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

// Define the state type
interface UserState {
  users: User[];
  pagination: PaginationState;
  loading: boolean;
  createLoading: boolean;
  updateLoading: boolean;
  deleteLoading: boolean;
  error: string | null;

  config: { header_key: string; label: string; header_type: string }[];
  filtersConfig: FilterConfig[];

  loadingFlags: {
    getUsersPaginated: boolean;
  };
}

export const getUsersPaginated = createAsyncThunk('users/getUsersPaginated', async (params: Record<string, any>) => {
  const res = await api.get('/users', { params: flattenQuery(params) });
  return res.data;
});

// Thunks for asynchronous operations
export const fetchUsers = createAsyncThunk<User[]>('users/fetchUsers', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/users');

    // Backward-compatible: support old array response OR new queues-style payload
    const data: any = response.data;
    const users = Array.isArray(data) ? data : data?.users;

    return (users || []) as User[];
  } catch (error: any) {
    return rejectWithValue(extractErrorMessage(error.response?.data, 'Error fetching users'));
  }
});

export const createUser = createAsyncThunk<User, Partial<User>>('users/createUser', async (user, { rejectWithValue }) => {
  try {
    const response = await api.post('/users', user);
    return response.data as User;
  } catch (error: any) {
    return rejectWithValue(extractErrorMessage(error.response?.data, 'Error creating user'));
  }
});

export const fetchUser = createAsyncThunk<User, number>('users/fetchUser', async (user_id, { rejectWithValue }) => {
  try {
    const response = await api.get(`/users/${user_id}`);
    return response.data as User;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || 'Error fetching user');
  }
});

export const updateUser = createAsyncThunk<User, User>('users/updateUser', async (user, { rejectWithValue }) => {
  try {
    const response = await api.put(`/users/${user.user_id}`, user);
    return response.data as User;
  } catch (error: any) {
    return rejectWithValue(extractErrorMessage(error.response?.data, 'Error updating user'));
  }
});

export const deleteUser = createAsyncThunk<number, number>('users/deleteUser', async (user_id, { rejectWithValue }) => {
  try {
    await api.delete(`/users/${user_id}`);
    return user_id;
  } catch (error: any) {
    return rejectWithValue(extractErrorMessage(error.response?.data, 'Error deleting user'));
  }
});

const initialState: UserState = {
  users: [],
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 2000,
  },
  loading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,
  error: null,

  config: [],
  filtersConfig: [],

  loadingFlags: {
    getUsersPaginated: false,
  },
};

const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch users (non-paginated)
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = extractErrorMessage(action.payload ?? action.error.message, 'Error fetching users');
      })

      .addCase(fetchUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.users.findIndex((user) => user.user_id === action.payload.user_id);
        if (index !== -1) {
          state.users[index] = action.payload;
        } else {
          state.users.push(action.payload);
        }
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.loading = false;
        state.error = extractErrorMessage(action.payload ?? action.error.message, 'Error fetching user');
      })

      // Paginated users (Queues-style)
      .addCase(getUsersPaginated.pending, (state) => {
        state.error = null;
        state.loadingFlags.getUsersPaginated = true;
      })
      .addCase(getUsersPaginated.fulfilled, (state, action) => {
        const { users, config, filtersConfig, pagination } = action.payload;

        state.users = users || [];
        state.config = config || [];
        state.filtersConfig = filtersConfig || [];

        state.pagination.totalItems = pagination?.total_records ?? 0;
        state.pagination.totalPages = pagination?.total_pages ?? 0;
        state.pagination.pageSize = pagination?.limit ?? state.pagination.pageSize;
        state.pagination.currentPage = (pagination?.current_page ?? 1) > (pagination?.total_pages ?? 0) ? 1 : (pagination?.current_page ?? 1);

        state.loadingFlags.getUsersPaginated = false;
      })
      .addCase(getUsersPaginated.rejected, (state, action) => {
        state.error = extractErrorMessage(action.payload ?? action.error.message, 'Failed to fetch users');
        state.loadingFlags.getUsersPaginated = false;
      })

      // Create user
      .addCase(createUser.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.createLoading = false;
        state.users.push(action.payload);
      })
      .addCase(createUser.rejected, (state) => {
        state.createLoading = false;
      })

      // Update user
      .addCase(updateUser.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.updateLoading = false;
        const index = state.users.findIndex((user) => user.user_id === action.payload.user_id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
      })
      .addCase(updateUser.rejected, (state) => {
        state.updateLoading = false;
      })

      // Delete user
      .addCase(deleteUser.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.users = state.users.filter((user) => user.user_id !== action.payload);
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = extractErrorMessage(action.payload ?? action.error.message, 'Error deleting user');
      });
  },
});

export default userSlice.reducer;
